/**
 * Production execution controller — framework-free so it can be unit-tested in node.
 * Rules: server is authoritative (no stock/reservation/close math here); one mutation at a
 * time; one idempotency key per submission attempt, preserved for retries of the same payload.
 */
import { ApiError } from '@/services/apiFetch'
import type {
  ExecutionState,
  IssueLineInput,
  MakeupRequest,
  RequirementSummary,
} from '@/services/productionOrders/executionApi'
import type { ProductionOrder } from '@/services/productionOrders'

export interface ExecutionApi {
  getOrder(id: string): Promise<ProductionOrder | null>
  getExecution(id: string): Promise<ExecutionState>
  start(id: string): Promise<ExecutionState>
  recordCompletion(id: string, qty: number, key: string): Promise<{ state: ExecutionState }>
  getRequirement(id: string): Promise<RequirementSummary | null>
  listMakeups(id: string): Promise<MakeupRequest[]>
  getMakeups?: (id: string) => Promise<MakeupRequest[]>
  requestMakeup(
    id: string,
    input: { material_id: string; qty_base: number; reason: string },
  ): Promise<MakeupRequest>
  reserveMakeup(id: string, requestId: string): Promise<MakeupRequest>
  issue(id: string, payload: { note?: string | null; lines: IssueLineInput[] }, key: string): Promise<unknown>
  close(id: string): Promise<ExecutionState>
}

export type MutationName = 'start' | 'completion' | 'makeup' | 'reserve-makeup' | 'issue' | 'close'

export interface ErrorInfo {
  code: string
  message: string
}

export interface ExecutionSnapshot {
  loading: boolean
  /** Page-level load error */
  loadError: ErrorInfo | null
  /** Last action error — stays visible until the next action */
  actionError: ErrorInfo | null
  notice: string | null
  order: ProductionOrder | null
  execution: ExecutionState | null
  requirement: RequirementSummary | null
  /** Persisted makeup requests loaded from server */
  makeups: MakeupRequest[]
  busy: MutationName | null
}

export function toErrorInfo(err: unknown): ErrorInfo {
  if (err instanceof ApiError) return { code: err.code, message: err.message }
  if (err instanceof Error) return { code: 'NETWORK_ERROR', message: err.message }
  return { code: 'UNKNOWN', message: 'Đã xảy ra lỗi không xác định' }
}

function defaultKey(): string {
  return globalThis.crypto?.randomUUID?.() ?? `k-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

/** One key per attempt signature; kept (for retries) until that attempt succeeds. */
export function createAttemptKeys(gen: () => string = defaultKey) {
  const keys = new Map<string, string>()
  return {
    keyFor(signature: string): string {
      let k = keys.get(signature)
      if (!k) {
        k = gen()
        keys.set(signature, k)
      }
      return k
    },
    clear(signature: string): void {
      keys.delete(signature)
    },
  }
}

export function parsePositiveQty(text: string): number | null {
  const n = Number(String(text).trim().replace(',', '.'))
  return Number.isFinite(n) && n > 0 ? n : null
}

export function issueSignature(planId: string, lines: IssueLineInput[]): string {
  const norm = [...lines]
    .map((l) => `${l.reservation_id}|${l.qty_base}|${l.makeup_request_id ?? ''}`)
    .sort()
  return `issue:${planId}:${norm.join(';')}`
}

export function createExecutionController(
  orderId: string,
  api: ExecutionApi,
  onChange: (s: ExecutionSnapshot) => void,
  genKey?: () => string,
) {
  const keys = createAttemptKeys(genKey)
  let snap: ExecutionSnapshot = {
    loading: true,
    loadError: null,
    actionError: null,
    notice: null,
    order: null,
    execution: null,
    requirement: null,
    makeups: [],
    busy: null,
  }
  const set = (patch: Partial<ExecutionSnapshot>) => {
    snap = { ...snap, ...patch }
    onChange(snap)
  }

  async function fetchAll() {
    const fetchMakeups = () =>
      api.listMakeups ? api.listMakeups(orderId) : api.getMakeups?.(orderId) ?? Promise.resolve([])
    const [order, execution, requirement, makeups] = await Promise.all([
      api.getOrder(orderId),
      api.getExecution(orderId),
      api.getRequirement(orderId),
      fetchMakeups(),
    ])
    if (!order) throw new ApiError('NOT_FOUND', 'Không tìm thấy lệnh sản xuất', 404)
    set({ order, execution, requirement, makeups: makeups ?? [], loading: false, loadError: null })
  }

  async function load() {
    set({ loading: true, loadError: null })
    try {
      await fetchAll()
    } catch (err) {
      set({ loading: false, loadError: toErrorInfo(err) })
    }
  }

  /** Runs one mutation; ignored while another is in flight (duplicate-click protection). */
  async function mutate(
    name: MutationName,
    fn: () => Promise<void>,
    successNotice: string,
    refresh = true,
  ): Promise<boolean> {
    if (snap.busy) return false
    set({ busy: name, actionError: null, notice: null })
    try {
      await fn()
    } catch (err) {
      set({ busy: null, actionError: toErrorInfo(err) })
      return false
    }
    try {
      if (refresh) await fetchAll()
    } catch (err) {
      set({ busy: null, notice: successNotice, actionError: toErrorInfo(err) })
      return true
    }
    set({ busy: null, notice: successNotice })
    return true
  }

  const fail = (code: string, message: string): false => {
    set({ actionError: { code, message }, notice: null })
    return false
  }

  return {
    getSnapshot: () => snap,
    load,

    start: () =>
      mutate(
        'start',
        async () => {
          await api.start(orderId)
        },
        'Đã bắt đầu sản xuất',
      ),

    async submitCompletion(qtyText: string): Promise<boolean> {
      if (snap.busy) return false
      const qty = parsePositiveQty(qtyText)
      if (qty === null) return fail('INVALID_COMPLETION_QTY', 'Số lượng hoàn thành phải lớn hơn 0')
      const sig = `completion:${orderId}:${qty}`
      const key = keys.keyFor(sig)
      return mutate(
        'completion',
        async () => {
          await api.recordCompletion(orderId, qty, key)
          keys.clear(sig)
        },
        'Đã ghi nhận số lượng hoàn thành',
      )
    },

    async requestMakeup(input: { materialId: string; qtyText: string; reason: string }): Promise<boolean> {
      if (snap.busy) return false
      const qty = parsePositiveQty(input.qtyText)
      if (!input.materialId) return fail('MATERIAL_REQUIRED', 'Chọn vật tư cần bổ sung')
      if (qty === null) return fail('INVALID_MAKEUP_QTY', 'Số lượng bổ sung phải lớn hơn 0')
      if (!input.reason.trim()) return fail('MAKEUP_REASON_REQUIRED', 'Nhập lý do bổ sung vật tư')
      return mutate(
        'makeup',
        async () => {
          await api.requestMakeup(orderId, {
            material_id: input.materialId,
            qty_base: qty,
            reason: input.reason.trim(),
          })
        },
        'Đã tạo yêu cầu bổ sung vật tư',
      )
    },

    reserveMakeup: (requestId: string) =>
      mutate(
        'reserve-makeup',
        async () => {
          await api.reserveMakeup(orderId, requestId)
        },
        'Đã giữ hàng khả dụng cho yêu cầu bổ sung',
      ),

    async issue(lines: IssueLineInput[], note?: string): Promise<boolean> {
      if (snap.busy) return false
      if (lines.length === 0) return fail('EMPTY_LINES', 'Chưa nhập số lượng xuất vật tư')
      const sig = issueSignature(orderId, lines)
      const key = keys.keyFor(sig)
      return mutate(
        'issue',
        async () => {
          await api.issue(orderId, { note: note?.trim() || null, lines }, key)
          keys.clear(sig)
        },
        'Đã xuất vật tư',
      )
    },

    close: () =>
      mutate(
        'close',
        async () => {
          await api.close(orderId)
        },
        'Đã đóng lệnh sản xuất',
      ),
  }
}
